import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, isAuthError, isForbiddenError, type RegistryAgent } from '../../api/client'
import { useAuthOptional } from '../../auth/AuthContext'
import AgentCommandCenter, { type AgentRosterState } from './agents/AgentCommandCenter'
import { useAgentWorkspaces } from './agents/useAgentWorkspaces'
import { useAgentActionQueue } from './agents/useAgentActionQueue'
import type { StageFormInput, StageSubmissionState } from './agents/AgentCommandStage'

/**
 * Square Code V2 Agents Overview — route wrapper (Agent Runtime
 * Reconciliation, 2026-07-14; restyled to the "neon void" design language
 * 2026-07-14; command stage + approval queue added, commit_2).
 *
 * Registered at /square/command-center/agents (AccessGate).
 *
 * Reads the real, existing roster (api.commandCenter.agents.list(), the
 * same endpoint Agent Control Center uses) and links out to the five
 * canonical, already-shipped agent surfaces: Agent Control Center,
 * Agent Workspace, Activation Doctor, Engine Runtime V4, and Operations
 * Agent Command. Pause/activate control stays on Agent Control Center.
 *
 * commit_2 adds two REAL, live sections on this page itself:
 *   - Command stage: prepares a real sq_agent_action_requests row via
 *     api.agentWorkspace.actionRequests.create(). api.commands.* (the
 *     canonical dispatcher) has no agent-directed verb, so this does
 *     not touch that dispatcher — it is the one governed path that
 *     actually exists for an agent-directed action.
 *   - Approval queue: reads + decides those same real rows via
 *     api.agentWorkspace.actionRequests.list() / .approve() — the exact
 *     endpoints Agent Workspace and Operations Agent Command's
 *     read-only queue already use. Self-approval denial and the atomic
 *     decision transition are enforced server-side (PR #129); this page
 *     only surfaces the server's own reasons honestly.
 *
 * This file owns every live hook (roster fetch, workspace resolution,
 * queue read/decide, stage submission) and navigation; all rendering
 * lives in the presentational ./agents/AgentCommandCenter tree, same
 * split as OperationsAgentCommandCenter / DailyMeetingsWorkroom.
 */

export default function SquareCodeAgentsOverviewRoute() {
  const navigate = useNavigate()
  const auth = useAuthOptional()
  const currentUserId = auth?.user?.id ?? null
  const roleLevel = auth?.bootstrap?.role?.level ?? null

  const [state, setState] = useState<AgentRosterState>('loading')
  const [agents, setAgents] = useState<RegistryAgent[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const result = await api.commandCenter.agents.list()
        if (cancelled) return
        setAgents(result.agents)
        setState('ready')
      } catch (err) {
        if (cancelled) return
        if (isAuthError(err)) return setState('auth_required')
        if (isForbiddenError(err)) return setState('forbidden')
        setErrorMessage(err instanceof ApiError ? err.message || err.code : 'request_failed')
        setState('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const workspaces = useAgentWorkspaces()
  const queue = useAgentActionQueue(workspaces.workspaces, workspaces.state === 'ready')
  const targetWorkspace = workspaces.workspaces[0] ?? null

  const [stageSubmission, setStageSubmission] = useState<StageSubmissionState>({ status: 'idle' })

  const handleStageSubmit = useCallback(
    async (input: StageFormInput) => {
      if (!targetWorkspace) return
      setStageSubmission({ status: 'submitting' })
      try {
        const targets = input.targets
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
        const result = await api.agentWorkspace.actionRequests.create({
          workspace_id: targetWorkspace.id,
          action_type: input.actionType,
          title: input.title,
          summary: input.summary,
          targets,
          prepared_by_agent_id: input.agentId || undefined,
        })
        if (!result.ok) {
          if (result.reason === 'unresolved_targets' && Array.isArray(result.unresolved)) {
            setStageSubmission({
              status: 'unresolved_targets',
              unresolved: result.unresolved.map((t) => ({
                input: typeof (t as Record<string, unknown>).input === 'string' ? String((t as Record<string, unknown>).input) : 'unknown target',
                reason: typeof (t as Record<string, unknown>).reason === 'string' ? String((t as Record<string, unknown>).reason) : null,
              })),
            })
          } else {
            setStageSubmission({ status: 'error', message: result.reason ?? 'request_failed' })
          }
          return
        }
        const created = result.action_request as Record<string, unknown> | undefined
        setStageSubmission({
          status: 'created',
          requestId: String(created?.id ?? ''),
          workspaceName: targetWorkspace.name,
          actionType: input.actionType,
        })
        await queue.reload()
      } catch (err) {
        setStageSubmission({
          status: 'error',
          message: err instanceof ApiError ? err.message || err.code : 'request_failed',
        })
      }
    },
    [targetWorkspace, queue],
  )

  return (
    <AgentCommandCenter
      state={state}
      agents={agents}
      errorMessage={errorMessage}
      onNavigate={(path) => navigate(path)}
      onBack={() => navigate(COMMAND_CENTER_ROOT)}
      stage={{
        roleLevel,
        workspaceState: workspaces.state,
        workspaceErrorMessage: workspaces.errorMessage,
        targetWorkspace,
        creatingWorkspace: workspaces.creating,
        createWorkspaceError: workspaces.createError,
        onCreateWorkspace: (name) => void workspaces.createWorkspace(name),
        submission: stageSubmission,
        onSubmit: (input) => void handleStageSubmit(input),
      }}
      queue={{
        currentUserId,
        roleLevel,
        workspaceState: workspaces.state,
        workspaceErrorMessage: workspaces.errorMessage,
        workspaces: workspaces.workspaces,
        queueState: queue.state,
        queueErrorMessage: queue.errorMessage,
        rows: queue.rows,
        decisions: queue.decisions,
        onDecide: (requestId, decision) => void queue.decide(requestId, decision),
        onReload: () => {
          void workspaces.reload()
          void queue.reload()
        },
      }}
    />
  )
}
